import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import PageHeader from "../components/common/PageHeader";
import { useControlCash, CURRENCY_OPTIONS } from "../store/controlCashStore";
import { useConfirm } from "../contexts/ConfirmContext";
import { useToast } from "../contexts/ToastContext";
import { getSafeArray } from "../utils/common";
import { groupByCurrency } from "../utils/currency";

// ============================================================================
// COMPONENTE: IncomeTemplateCard
// ============================================================================
const IncomeTemplateCard = ({ template, onEdit, onDelete, onToggleActive, formatAmount }) => {
  // Colores diferentes por moneda para el header
  const currencyColors = {
    PEN: "from-emerald-500 to-teal-600",
    USD: "from-blue-500 to-indigo-600",
    COP: "from-yellow-500 to-orange-600",
    ARS: "from-sky-500 to-cyan-600",
    CLP: "from-red-500 to-pink-600",
    VEF: "from-purple-500 to-violet-600"
  };
  
  const currencyCode = template.currency || "PEN";
  const gradientClass = currencyColors[currencyCode] || "from-gray-500 to-gray-600";

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200/50 dark:border-gray-700/50 hover:shadow-md transition-all overflow-hidden ${!template.active ? "opacity-60" : ""}`}>
      <div className={`bg-gradient-to-r ${gradientClass} px-3 py-2 flex items-center justify-between`}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-xs font-semibold text-white truncate">{template.name}</span>
          {template.active ? (
            <span className="px-1.5 py-0.5 bg-white/20 text-white text-[10px] font-medium rounded-full whitespace-nowrap">
              Activo
            </span>
          ) : (
            <span className="px-1.5 py-0.5 bg-black/20 text-white text-[10px] font-medium rounded-full whitespace-nowrap">
              Inactivo
            </span>
          )}
        </div>
      </div>

      <div className="p-3">
        <div className="flex items-start justify-between mb-2">
          <span className="text-base font-bold text-gray-900 dark:text-white flex-1 mr-2">
            {formatAmount(template.amount, undefined, currencyCode)}
          </span>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => onToggleActive(template.id)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
              template.active ? "bg-green-500" : "bg-gray-300"
            }`}
            aria-label={template.active ? "Desactivar" : "Activar"}
            aria-pressed={template.active}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                template.active ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onEdit(template)}
              className="px-2 py-1 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-xs font-medium rounded transition-colors"
              aria-label={`Editar ${template.name}`}
            >
              Editar
            </button>
            <button
              onClick={() => onDelete(template.id, template.name)}
              className="px-2 py-1 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 text-xs font-medium rounded transition-colors"
              aria-label={`Eliminar ${template.name}`}
            >
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE: IncomeTemplateModal
// ============================================================================
const IncomeTemplateModal = ({ isOpen, onClose, onSave, editingTemplate, currency, toDisplayAmount, enabledCurrencies = ["PEN", "USD"] }) => {
  const [formData, setFormData] = useState({ name: "", amount: "", currency: "PEN", active: true });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingTemplate) {
      setFormData({
        name: editingTemplate.name,
        amount: String(editingTemplate.amount),
        currency: editingTemplate.currency || "PEN",
        active: editingTemplate.active
      });
    } else {
      setFormData({ name: "", amount: "", currency: "PEN", active: true });
    }
    setErrors({});
  }, [editingTemplate, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Nombre requerido";
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount < 0) newErrors.amount = "Monto inválido";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave({
      name: formData.name.trim(),
      amount: amount,
      currency: formData.currency || "PEN",
      active: formData.active
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            {editingTemplate ? "Editar ingreso" : "Nuevo ingreso recurrente"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="Ej: Sueldo, Freelance, Alquiler..."
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 ${errors.name ? "border-red-300" : "border-gray-200 focus:border-green-500"}`}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Monto mensual</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                    {CURRENCY_OPTIONS.find(opt => opt.code === formData.currency)?.symbol || "S/"}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))}
                    placeholder="0.00"
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 ${errors.amount ? "border-red-300" : "border-gray-200 focus:border-green-500"}`}
                  />
                </div>
                {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Moneda</label>
                <div className="relative">
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData(p => ({ ...p, currency: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-green-500 focus:ring-green-200 transition-all appearance-none bg-white cursor-pointer"
                  >
                    {CURRENCY_OPTIONS.filter(opt => enabledCurrencies.includes(opt.code)).map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <svg 
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            {editingTemplate && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFormData(p => ({ ...p, active: !p.active }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full ${formData.active ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.active ? "translate-x-6" : "translate-x-1"}`} />
                </button>
                <span className="text-sm font-medium text-gray-700">Activo</span>
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl">
                {editingTemplate ? "Guardar" : "Crear"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PÁGINA PRINCIPAL
// ============================================================================
const RecurringIncomes = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { state, dispatch, formatDisplayAmount } = useControlCash();
  const { confirm } = useConfirm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const templates = getSafeArray(state.recurringIncomeTemplates);
  const enabledCurrencies = state.enabledCurrencies || ["PEN", "USD"];
  const activeTemplates = templates.filter(t => t.active);
  // Agrupar templates activos por moneda
  const templatesByCurrency = groupByCurrency(activeTemplates);

  const handleSave = (data) => {
    if (editingTemplate) {
      dispatch({
        type: "UPDATE_RECURRING_INCOME_TEMPLATE",
        payload: { 
          id: editingTemplate.id, 
          patch: { 
            name: data.name, 
            amount: data.amount, 
            currency: data.currency || "PEN",
            active: data.active 
          } 
        }
      });
    } else {
      dispatch({
        type: "CREATE_RECURRING_INCOME_TEMPLATE",
        payload: { 
          name: data.name, 
          amount: data.amount, 
          currency: data.currency || "PEN",
          active: data.active 
        }
      });
    }
    setEditingTemplate(null);
    setIsModalOpen(false);
  };

  const handleDelete = async (id, name) => {
    const ok = await confirm({
      title: "Eliminar ingreso recurrente",
      message: `¿Eliminar "${name}"?`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      try {
        await dispatch({ type: "DELETE_RECURRING_INCOME_TEMPLATE", payload: id });
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Error al eliminar. Intenta de nuevo.");
      }
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <PageHeader 
          onBack={() => navigate("/")}
          title="Ingresos recurrentes"
          subtitle="Crea ingresos que se repiten cada mes. Se agregarán automáticamente al crear un nuevo mes."
        />
        {/* Resumen compacto de estado */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800/50">
            <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-semibold text-green-700 dark:text-green-400">
              {activeTemplates.length} {activeTemplates.length === 1 ? "activo" : "activos"}
            </span>
          </div>
        </div>

        {/* Totales por moneda - diseño destacado */}
        <div className="mb-10">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            Total mensual estimado
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {enabledCurrencies.map((currencyCode) => {
              const total = templatesByCurrency[currencyCode] || 0;
              const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === currencyCode);
              const currencyLabel = currencyInfo?.label || currencyCode;
              const currencySymbol = currencyInfo?.symbol || currencyCode;
              
              // Colores diferentes por moneda
              const currencyColors = {
                PEN: "from-emerald-500 to-teal-600 border-emerald-400/20",
                USD: "from-blue-500 to-indigo-600 border-blue-400/20",
                COP: "from-yellow-500 to-orange-600 border-yellow-400/20",
                ARS: "from-sky-500 to-cyan-600 border-sky-400/20",
                CLP: "from-red-500 to-pink-600 border-red-400/20",
                VEF: "from-purple-500 to-violet-600 border-purple-400/20"
              };
              
              const gradientClass = currencyColors[currencyCode] || "from-gray-500 to-gray-600 border-gray-400/20";
              
              return (
                <div
                  key={currencyCode}
                  className={`bg-gradient-to-br ${gradientClass} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <span className="text-xl font-bold text-white">
                          {currencySymbol}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-white/90 uppercase tracking-wide">
                        {currencyLabel}
                      </p>
                    </div>
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-white mb-1">
                    {formatDisplayAmount(total, undefined, currencyCode)}
                  </p>
                  <p className="text-xs text-white/70">
                    {activeTemplates.filter(t => t.currency === currencyCode).length} {activeTemplates.filter(t => t.currency === currencyCode).length === 1 ? "ingreso recurrente" : "ingresos recurrentes"}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Mis ingresos recurrentes</h2>
          <button
            onClick={() => { setEditingTemplate(null); setIsModalOpen(true); }}
            className="px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo ingreso
          </button>
        </div>

        {/* Ingresos agrupados por moneda */}
        {templates.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-8 text-center">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">No hay ingresos recurrentes. Crea plantillas como Sueldo o Freelance.</p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl shadow-lg shadow-blue-500/25 transition-all"
            >
              Crear primer ingreso recurrente
            </button>
          </div>
        ) : (
          enabledCurrencies.map((currencyCode) => {
            const currencyTemplates = templates.filter(t => (t.currency || "PEN") === currencyCode);
            if (currencyTemplates.length === 0) return null;
            
            const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === currencyCode);
            const currencyLabel = currencyInfo?.label || currencyCode;
            
            return (
              <div key={currencyCode} className="mb-8">
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <span className="text-lg">{currencyInfo?.symbol || currencyCode}</span>
                  <span>{currencyLabel}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 font-normal">
                    ({currencyTemplates.length} {currencyTemplates.length === 1 ? "ingreso" : "ingresos"})
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {currencyTemplates.map(t => (
                    <IncomeTemplateCard
                      key={t.id}
                      template={t}
                      onEdit={(tpl) => { setEditingTemplate(tpl); setIsModalOpen(true); }}
                      onDelete={handleDelete}
                      onToggleActive={(id) => dispatch({ type: "TOGGLE_RECURRING_INCOME_TEMPLATE", payload: id })}
                      formatAmount={(amount) => formatDisplayAmount(amount, false, t.currency || "PEN")}
                    />
                  ))}
                </div>
              </div>
            );
          })
        )}

        <IncomeTemplateModal
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setEditingTemplate(null); }}
          onSave={handleSave}
          editingTemplate={editingTemplate}
          currency={state.currencySymbol ?? state.currency}
          enabledCurrencies={state.enabledCurrencies || ["PEN", "USD"]}
        />
      </div>
    </Layout>
  );
};

export default RecurringIncomes;
